import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Card, Col, Flex, Grid, Row, theme } from 'antd';
import type { BannerActionDetailOut, BannerOut } from '@/api/generated/schemas';
import { BannerBasicInfoCard } from '@/components/user_catalog/banners/BannerBasicInfoCard';
import { BannerLinksTable } from '@/components/user_catalog/banners/BannerLinksTable';
import { BannerPreviewPanel } from '@/components/user_catalog/banners/BannerPreviewPanel';
import {
  buildLinkRows,
  clampPage,
} from '@/components/user_catalog/banners/bannerPreview';
import type { PreviewModel } from '@/components/user_catalog/banners/bannerPreview';

interface BannerGeneralTabProps {
  record: BannerOut;
  model: PreviewModel;
  bannerActionsMap: Map<number, BannerActionDetailOut>;
  actionsLoading: boolean;
}

export const BannerGeneralTab = ({
  record,
  model,
  bannerActionsMap,
  actionsLoading,
}: BannerGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const linkRows = buildLinkRows(record.banner_links, bannerActionsMap, model);

  // Clamped on read: a refetch can shrink the carousel under a
  // selection that was valid a moment ago.
  const [page, setPage] = useState(1);
  const selectedPage = clampPage(page, model.entries.length);

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <BannerBasicInfoCard record={record} model={model} />
          {linkRows.length > 0 && (
            <Card
              title={`${t('user_catalog.banners.banner_links.title')} (${linkRows.length})`}
            >
              <BannerLinksTable
                rows={linkRows}
                effectiveSize={model.effectiveSize}
                selectedPage={selectedPage}
              />
            </Card>
          )}
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <BannerPreviewPanel
          model={model}
          selectedPage={selectedPage}
          onSelectPage={setPage}
          loading={actionsLoading}
        />
      </Col>
    </Row>
  );
};
